import React from "react";
import { RotateCcw, AlertTriangle } from "lucide-react";
import { Branch, Chips, TreeHeader } from "./FaultTreePrimitives";

/**
 * Brakes / Pedal feel fault tree — DEEP, 6 branching levels.
 *
 *   1. Primary symptom (noise / pedal feel / pulling / warning light / loss)
 *   2. Noise type (squeal / grind / no noise)
 *   3. Pedal feel (firm / spongy / sinks / sticky / vibration)
 *   4. Pulling direction (none / left / right / under braking only)
 *   5. ABS / brake light status (none / ABS / brake / both)
 *   6. Recent context (after pads/discs / after wet / longer ago)
 */
export default function BrakesFaultTree({
  data = {},
  onChange = () => {},
  onReset,
}) {
  const showNoise = data.primary === "noise";
  const showPedal = data.primary === "pedal_feel" || data.primary === "loss";
  const showPull = !!data.primary;
  const showLights = !!data.primary;
  const showContext = !!data.primary;
  const showNotes = !!data.primary;

  // Critical safety banner
  const isCritical =
    data.primary === "loss" ||
    data.pedal_feel === "sinks" ||
    data.warning_lights === "brake" ||
    data.warning_lights === "both";

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="brakes-fault-tree"
    >
      <TreeHeader
        eyebrow="Diagnostic · Fault tree"
        title="Brakes / Pedal feel triage"
        tone="rose"
        right={
          onReset && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-800"
              data-testid="brakes-reset"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )
        }
      />

      <div className="p-4 space-y-3.5">
        <Branch
          step={1}
          label="Primary symptom"
          subtitle="Brakes can fail multiple ways at once — pick the loudest signal."
          testId="branch-br-primary"
        >
          <Chips
            tone="rose"
            value={data.primary}
            options={[
              { v: "noise", label: "Noise when braking" },
              { v: "pedal_feel", label: "Pedal feel change" },
              { v: "pulling", label: "Pulling to one side" },
              { v: "warning_light", label: "Warning light" },
              { v: "vibration", label: "Vibration when braking" },
              { v: "loss", label: "Loss of braking power" },
            ]}
            onChange={(v) =>
              onChange({
                primary: v,
                noise_type: null,
                pedal_feel: null,
                pulling_direction: null,
                warning_lights: null,
                recent_context: null,
              })
            }
            testId="br-primary"
          />
        </Branch>

        {isCritical && (
          <div
            className="rounded-md border border-rose-300 bg-rose-50 px-3.5 py-2.5 flex items-start gap-2.5"
            data-testid="brakes-critical-banner"
          >
            <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="text-[13.5px] text-rose-900 leading-snug">
              <p className="font-semibold">Brake safety — recover, do not drive.</p>
              <p>
                Loss of braking, sinking pedal, or a red brake light means the vehicle
                is not safe to drive — offer recovery, not collection.
              </p>
            </div>
          </div>
        )}

        {showNoise && (
          <Branch step={2} label="What does the noise sound like?" testId="branch-br-noise">
            <Chips
              tone="rose"
              value={data.noise_type}
              options={[
                { v: "squeal", label: "Squeal / squeak" },
                { v: "grind", label: "Grinding / metal-on-metal" },
                { v: "thud", label: "Thud / clunk" },
                { v: "rattle", label: "Rattle" },
                { v: "intermittent", label: "Only sometimes" },
              ]}
              onChange={(v) => onChange({ noise_type: v })}
              testId="br-noise"
            />
          </Branch>
        )}

        {showPedal && (
          <Branch step={3} label="Pedal feel" testId="branch-br-pedal">
            <Chips
              tone="rose"
              value={data.pedal_feel}
              options={[
                { v: "firm_normal", label: "Firm / normal" },
                { v: "spongy", label: "Spongy / soft" },
                { v: "sinks", label: "Sinks to the floor" },
                { v: "sticky", label: "Sticky / slow to return" },
                { v: "vibrates", label: "Vibrates under foot" },
                { v: "high", label: "Sits higher than usual" },
              ]}
              onChange={(v) => onChange({ pedal_feel: v })}
              testId="br-pedal"
            />
          </Branch>
        )}

        {showPull && (
          <Branch step={4} label="Does it pull when braking?" testId="branch-br-pull">
            <Chips
              tone="rose"
              value={data.pulling_direction}
              options={[
                { v: "none", label: "No, straight line" },
                { v: "left", label: "Pulls left" },
                { v: "right", label: "Pulls right" },
                { v: "either", label: "Either side — variable" },
              ]}
              onChange={(v) => onChange({ pulling_direction: v })}
              testId="br-pull"
            />
          </Branch>
        )}

        {showLights && (
          <Branch step={5} label="Brake / ABS warning light?" testId="branch-br-lights">
            <Chips
              tone="rose"
              value={data.warning_lights}
              options={[
                { v: "none", label: "None on" },
                { v: "abs", label: "ABS light on" },
                { v: "brake", label: "Brake light on" },
                { v: "both", label: "Both on" },
                { v: "intermittent", label: "Comes on & off" },
              ]}
              onChange={(v) => onChange({ warning_lights: v })}
              testId="br-lights"
            />
          </Branch>
        )}

        {showContext && (
          <Branch step={6} label="Recent context" testId="branch-br-context">
            <Chips
              tone="rose"
              value={data.recent_context}
              options={[
                { v: "after_pads", label: "After recent pads / discs" },
                { v: "after_mot", label: "Since recent MOT" },
                { v: "after_wet", label: "Started after wet weather" },
                { v: "gradual", label: "Gradual over weeks" },
                { v: "sudden", label: "Came on suddenly" },
              ]}
              onChange={(v) => onChange({ recent_context: v })}
              testId="br-context"
            />
          </Branch>
        )}

        {showNotes && (
          <Branch step={7} label="Customer’s own words" testId="branch-br-notes">
            <textarea
              rows={2}
              value={data.notes || ""}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="e.g. ‘pedal goes almost to the floor first thing in the morning then comes back’"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-rose-500"
              data-testid="br-notes"
            />
          </Branch>
        )}
      </div>
    </div>
  );
}
